/**
 * Authentication API Service
 *
 * Handles communication with SOKA backend for authentication.
 * Uses axios with automatic token refresh on 401 errors via interceptors.
 */

import axios, { type AxiosInstance } from 'axios';
import { setupRequestInterceptor, setupResponseInterceptor } from './auth-interceptors';

/**
 * Create axios instance for SOKA API with auth interceptors
 */
const createApiClient = (): AxiosInstance => {
  const baseURL = import.meta.env.VITE_SOKA_API_URL ?? 'https://dev.soka.club/api';

  const instance = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json'
    }
  });

  // Setup interceptors from separate module
  setupRequestInterceptor(instance);
  setupResponseInterceptor(instance);

  return instance;
};

// Singleton instance
const apiClient = createApiClient();

// Auth API endpoints
export interface RegisterPayload {
  wallet: string;
  typeOfLogin: string;
  source: 'PWA' | 'WEBSITE' | 'MOBILE' | 'FACTION';
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
}

export interface UserProfile {
  id: number;
  email: string;
  pseudo?: string;
  walletAddress: string;
  points: number;
  country?: string;
}

export const authApi = {
  /**
   * Register with SOKA backend using Web3Auth idToken
   */
  register: async (idToken: string, payload: RegisterPayload): Promise<AuthResponse> => {
    const response = await apiClient.post<AuthResponse>('/auth/register', payload, {
      headers: {
        Authorization: `Bearer ${idToken}`
      }
    });
    return response.data;
  },

  /**
   * Logout from SOKA backend
   */
  logout: async (): Promise<void> => {
    await apiClient.get('/auth/logout');
  },

  /**
   * Get current user profile
   */
  getProfile: async (): Promise<UserProfile> => {
    const response = await apiClient.get<UserProfile>('/auth/profile');
    return response.data;
  },

  /**
   * Refresh access token
   */
  refresh: async (refreshToken: string): Promise<AuthResponse> => {
    const response = await apiClient.get<AuthResponse>('/auth/refresh', {
      headers: {
        Authorization: `Bearer ${refreshToken}`
      }
    });
    return response.data;
  },

  /**
   * Update user profile (pseudo and country)
   * Used for first-time login profile completion
   */
  updateProfile: async (pseudo: string, country: string): Promise<UserProfile> => {
    const response = await apiClient.post<UserProfile>('/users/update-pseudo', {
      pseudo,
      country
    });
    return response.data;
  }
};

export { apiClient };
